import { useCallback, useEffect, useMemo, useState } from "react";
import CasaMapaAdminAnalyticsDateToolbar from "../../../components/casamapa/admin/CasaMapaAdminAnalyticsDateToolbar";
import CasaMapaAdminAnalyticsSectionShell from "../../../components/casamapa/admin/CasaMapaAdminAnalyticsSectionShell";
import CasaMapaMedianPriceTrendChart from "../../../components/casamap/market/CasaMapaMedianPriceTrendChart";
import {
  getCasaMapaAdminAnalyticsMarket,
  getCasaMapaAdminAnalyticsMarketTrend,
  getCasaMapaAdminAnalyticsSearches,
  getCasaMapaAdminAnalyticsTimeOnMarket,
  getCasaMapaAdminAnalyticsTraffic,
} from "../../../api/clientCasamapaAdminAnalyticsApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useCasaMapaAnalyticsRange } from "../../../hooks/useCasaMapaAnalyticsRange";
import { useLocale, useCurrency } from "../../../intl";
import "../../../clientViewRuntime/styles/casaMapaMarketIntelligence.css";

function MetricCard({ label, value, hint }) {
  return (
    <article className="cm-admin-analytics__metric">
      <span className="cm-admin-analytics__metric-label">{label}</span>
      <strong className="cm-admin-analytics__metric-value">{value ?? "—"}</strong>
      {hint ? <small className="cm-admin-analytics__metric-hint">{hint}</small> : null}
    </article>
  );
}

function formatCount(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString();
}

export default function CasaMapaAdminMarketAnalysisPage() {
  const { clientViewId } = useCasaMapaAdminPortal();
  const { t, locale } = useLocale();
  const { formatMoney } = useCurrency();
  const { range, activePreset, presets, handlePreset, updateStartDate, updateEndDate } =
    useCasaMapaAnalyticsRange(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [market, setMarket] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [searches, setSearches] = useState(null);
  const [timeOnMarket, setTimeOnMarket] = useState(null);
  const [trendPoints, setTrendPoints] = useState([]);
  const [trendStatus, setTrendStatus] = useState("loading");

  const loadData = useCallback(async () => {
    if (!clientViewId) return;
    setLoading(true);
    setError("");
    const params = { startDate: range.startDate, endDate: range.endDate };
    try {
      const [marketBody, trafficBody, searchesBody, tomBody] = await Promise.all([
        getCasaMapaAdminAnalyticsMarket(clientViewId, params),
        getCasaMapaAdminAnalyticsTraffic(clientViewId, params),
        getCasaMapaAdminAnalyticsSearches(clientViewId, params),
        getCasaMapaAdminAnalyticsTimeOnMarket(clientViewId, params),
      ]);
      setMarket(marketBody);
      setTraffic(trafficBody);
      setSearches(searchesBody);
      setTimeOnMarket(tomBody);
    } catch (err) {
      setError(err.message || t("realEstate.casaMapa.admin.analytics.errors.load"));
      setMarket(null);
      setTraffic(null);
      setSearches(null);
      setTimeOnMarket(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, range.endDate, range.startDate, t]);

  const loadTrend = useCallback(async () => {
    if (!clientViewId) return;
    setTrendStatus("loading");
    try {
      const body = await getCasaMapaAdminAnalyticsMarketTrend(clientViewId, { days: 365 });
      setTrendPoints(Array.isArray(body.points) ? body.points : []);
      setTrendStatus("ready");
    } catch {
      setTrendPoints([]);
      setTrendStatus("error");
    }
  }, [clientViewId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadTrend();
  }, [loadTrend]);

  const topSearchTerms = useMemo(() => {
    const terms = searches?.topQueries || searches?.queries || [];
    return Array.isArray(terms) ? terms.slice(0, 8) : [];
  }, [searches]);

  const pageViewTotal = useMemo(
    () =>
      (traffic?.viewsOverTime || []).reduce((sum, row) => sum + (row.pageViews || 0), 0),
    [traffic]
  );

  const searchTotal = useMemo(
    () => (searches?.topQueries || []).reduce((sum, row) => sum + (row.searchCount || 0), 0),
    [searches]
  );

  const toolbar = (
    <CasaMapaAdminAnalyticsDateToolbar
      range={range}
      activePreset={activePreset}
      presets={presets}
      onPreset={handlePreset}
      onStartDateChange={updateStartDate}
      onEndDateChange={updateEndDate}
    />
  );

  return (
    <CasaMapaAdminAnalyticsSectionShell
      introKey="realEstate.casaMapa.admin.analytics.market.subtitle"
      toolbar={toolbar}
      error={error}
      loading={loading}
    >
      <div className="cm-admin-analytics__metric-grid">
        <MetricCard
          label={t("realEstate.casaMapa.admin.analytics.market.activeListings")}
          value={formatCount(market?.activeListings)}
          hint={t("realEstate.casaMapa.admin.analytics.market.activeListingsHint")}
        />
        <MetricCard
          label={t("realEstate.casaMapa.admin.analytics.market.pageViews")}
          value={formatCount(pageViewTotal)}
        />
        <MetricCard
          label={t("realEstate.casaMapa.admin.analytics.market.searches")}
          value={formatCount(searchTotal)}
        />
        <MetricCard
          label={t("realEstate.casaMapa.admin.analytics.market.medianTom")}
          value={
            timeOnMarket?.observedTimeOnMarket?.medianDays != null
              ? `${timeOnMarket.observedTimeOnMarket.medianDays} ${t("realEstate.casaMapa.admin.analytics.market.days")}`
              : "—"
          }
          hint={timeOnMarket?.observedTimeOnMarket?.label}
        />
      </div>

      <article className="cm-admin-analytics__card cm-market-trend">
        <div className="cm-market-trend__head">
          <div>
            <h3>{t("realEstate.casaMapa.admin.analytics.market.priceTrend")}</h3>
            <p className="cm-muted">{t("realEstate.casaMapa.admin.analytics.market.priceTrendHint")}</p>
          </div>
          <span className="cm-market-trend__period">12m</span>
        </div>
        {trendStatus === "loading" ? (
          <p className="muted">{t("realEstate.casaMapa.admin.loading")}</p>
        ) : (
          <CasaMapaMedianPriceTrendChart
            points={trendPoints}
            locale={locale}
            formatMoney={formatMoney}
            t={t}
          />
        )}
      </article>

      <div className="cm-admin-analytics__split">
        <article className="cm-admin-analytics__card">
          <h4>{t("realEstate.casaMapa.admin.analytics.market.supplyByType")}</h4>
          {(market?.byListingType || []).length ? (
            <table className="cm-admin-analytics__table">
              <thead>
                <tr>
                  <th>{t("realEstate.casaMapa.admin.table.type")}</th>
                  <th>{t("realEstate.casaMapa.admin.analytics.market.count")}</th>
                  <th>{t("realEstate.casaMapa.admin.table.price")}</th>
                  <th>{t("realEstate.casaMapa.admin.analytics.market.pricePerM2")}</th>
                </tr>
              </thead>
              <tbody>
                {(market?.byListingType || []).map((row) => (
                  <tr key={row.listingType}>
                    <td>{row.listingType}</td>
                    <td>{formatCount(row.count)}</td>
                    <td>{row.medianPrice != null ? formatMoney(row.medianPrice) : "—"}</td>
                    <td>{row.medianPricePerM2 != null ? formatMoney(row.medianPricePerM2) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted">{t("realEstate.casaMapa.admin.analytics.market.noSupplyData")}</p>
          )}
        </article>

        <article className="cm-admin-analytics__card">
          <h4>{t("realEstate.casaMapa.admin.analytics.market.demandSignals")}</h4>
          <div className="cm-admin-analytics__metric-grid cm-admin-analytics__metric-grid--compact">
            <MetricCard
              label={t("realEstate.casaMapa.admin.analytics.market.priceIncreases")}
              value={formatCount(market?.priceChanges?.increases)}
            />
            <MetricCard
              label={t("realEstate.casaMapa.admin.analytics.market.priceDecreases")}
              value={formatCount(market?.priceChanges?.decreases)}
            />
          </div>
          {topSearchTerms.length ? (
            <ul className="cm-admin-analytics__list">
              {topSearchTerms.map((item) => (
                <li key={item.query || item.term}>
                  <span>{item.query || item.term}</span>
                  <strong>{formatCount(item.searchCount || item.count)}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t("realEstate.casaMapa.admin.analytics.market.noSearchData")}</p>
          )}
        </article>
      </div>

      {market?.note ? <p className="muted cm-admin-analytics__note">{market.note}</p> : null}
    </CasaMapaAdminAnalyticsSectionShell>
  );
}
